/**
 * Reaction Bar Component
 * Displays emoji reactions on a message with counts
 */

import { useState, useRef, useCallback } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useToggleReaction } from '@/hooks/useReactions';
import EmojiPicker from './EmojiPicker';
import type { MessageReaction } from '@/types/api.types';

interface ReactionBarProps {
  messageId: string;
  reactions: MessageReaction[];
  displayOnly?: boolean; // When true, only show existing reactions (no add button)
}

interface GroupedReaction {
  emoji: string;
  count: number;
  userIds: string[];
  userNames: string[];
  hasUserReacted: boolean;
}

/** Hover popover showing who reacted with what */
const ReactionPill = ({
  reaction,
  isPending,
  onClick,
  allReactions,
}: {
  reaction: GroupedReaction;
  isPending: boolean;
  onClick: () => void;
  allReactions: GroupedReaction[];
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = useCallback(() => {
    if (leaveTimeout.current) {
      clearTimeout(leaveTimeout.current);
      leaveTimeout.current = null;
    }
    hoverTimeout.current = setTimeout(() => setShowTooltip(true), 300);
  }, []);

  const handleLeave = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    leaveTimeout.current = setTimeout(() => setShowTooltip(false), 100);
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={onClick}
        disabled={isPending}
        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-sm transition-all ${
          reaction.hasUserReacted
            ? 'bg-blue-100 border border-blue-500 text-blue-700 dark:bg-blue-900/30 dark:border-blue-400 dark:text-blue-300'
            : 'bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 dark:bg-dark-bg-tertiary dark:border-dark-border-secondary dark:text-dark-text-secondary dark:hover:bg-dark-bg-hover'
        } ${isPending ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span>{reaction.emoji}</span>
        <span className="text-xs font-medium">{reaction.count}</span>
      </button>

      {showTooltip && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white dark:bg-dark-bg-elevated border border-gray-200 dark:border-dark-border-primary rounded-lg shadow-lg z-50 py-2 px-3 min-w-[140px] max-w-[220px] animate-reaction-popup"
          onMouseEnter={() => {
            if (leaveTimeout.current) {
              clearTimeout(leaveTimeout.current);
              leaveTimeout.current = null;
            }
          }}
          onMouseLeave={handleLeave}
        >
          <div className="text-xs font-semibold text-gray-500 dark:text-dark-text-muted mb-1.5 border-b border-gray-100 dark:border-dark-border-secondary pb-1.5">
            Реакции
          </div>
          <div className="space-y-1 max-h-[160px] overflow-y-auto">
            {allReactions.map((r) =>
              r.userNames.map((name, i) => (
                <div
                  key={`${r.emoji}-${r.userIds[i]}`}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="text-gray-800 dark:text-dark-text-primary truncate">{name}</span>
                  <span className="text-base flex-shrink-0">{r.emoji}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReactionBar = ({ messageId, reactions, displayOnly = false }: ReactionBarProps) => {
  const { user } = useAuthStore();
  const { mutate: toggleReaction, isPending } = useToggleReaction();
  const [showPicker, setShowPicker] = useState(false);

  // Group reactions by emoji
  const groupedReactions: GroupedReaction[] = reactions.reduce((acc, reaction) => {
    const existing = acc.find((r) => r.emoji === reaction.emoji);
    const hasUserReacted = reaction.user_id === user?.id;

    if (existing) {
      existing.count++;
      existing.userIds.push(reaction.user_id);
      existing.userNames.push(reaction.users?.display_name || 'Unknown');
      if (hasUserReacted) {
        existing.hasUserReacted = true;
      }
    } else {
      acc.push({
        emoji: reaction.emoji,
        count: 1,
        userIds: [reaction.user_id],
        userNames: [reaction.users?.display_name || 'Unknown'],
        hasUserReacted,
      });
    }
    return acc;
  }, [] as GroupedReaction[]);

  const handleReactionClick = (emoji: string) => {
    if (isPending) return;
    toggleReaction({ messageId, emoji });
  };

  const handleEmojiSelect = (emoji: string) => {
    toggleReaction({ messageId, emoji });
  };

  // If displayOnly mode and no reactions, don't render anything
  if (displayOnly && groupedReactions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1 relative">
      {/* Existing reactions */}
      {groupedReactions.map((reaction) => (
        <ReactionPill
          key={reaction.emoji}
          reaction={reaction}
          isPending={isPending}
          onClick={() => handleReactionClick(reaction.emoji)}
          allReactions={groupedReactions}
        />
      ))}

      {/* Add reaction button (only shown when NOT in displayOnly mode) */}
      {!displayOnly && (
        <div className="relative">
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-colors"
            title="Добави реакция"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>

          {/* Emoji picker popover */}
          {showPicker && (
            <EmojiPicker
              onEmojiSelect={handleEmojiSelect}
              onClose={() => setShowPicker(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ReactionBar;
